/* eslint-disable valid-jsdoc */
import hsv2rgb from 'hsv2rgb';
import {Injectable} from '@angular/core';

import {AdjusterPresetsService, Factor} from './adjuster-presets.service';
import {decimal2prettyPerc, nuts3IndexLayer, perc2color} from '../app.config';

type FakeLegend = Array<{
  color: string;
  name: string;
}>;

@Injectable({providedIn: 'root'})
export class AdjusterLegendService {
  /** https://colorbrewer2.org/?type=qualitative&scheme=Paired&n=12 */
  colorPalette = [
    '#a6cee3',
    '#1f78b4',
    '#b2df8a',
    '#33a02c',
    '#fb9a99',
    '#e31a1c',
    '#fdbf6f',
    '#ff7f00',
    '#cab2d6',
    '#6a3d9a',
    '#ffff99',
    '#b15928',
  ];
  serviceUrl: string;
  staticIndexLegend: any[];
  staticClusterLegend: any[];

  constructor(public adjusterPresetsService: AdjusterPresetsService) {
    this.serviceUrl = this.adjusterPresetsService.serviceBaseUrl + 'georeport/';
    this.staticIndexLegend = [...Array(50).keys()].map((num) => {
      return perc2color(num / 50);
    });
    this.staticClusterLegend = this.colorPalette;
  }

  createIndexLegend(): FakeLegend {
    return [
      {color: perc2color(1), name: 'best'},
      {color: perc2color(0.75), name: 'good'},
      {color: perc2color(0.5), name: 'mediocre'},
      {color: perc2color(0.25), name: 'bad'},
      {color: perc2color(0), name: 'worst'},
    ];
  }

  createClusterLegend(count: number): FakeLegend {
    const legend = [];
    let i = 0;
    while (i < count) {
      legend.push({color: this.colorPalette[i], name: `Cluster ${i + 1}`});
      i++;
    }
    return legend;
  }

  updateIndexLayerPopUps(factors: Factor[]) {
    const attrs = [
      {attribute: 'CNTR_CODE', label: 'Country'},
      {attribute: 'NUTS_NAME', label: 'Name'},
      {
        attribute: 'aggregate',
        label: 'aggregated index',
        displayFunction: decimal2prettyPerc,
      },
    ];
    for (const factor of factors) {
      attrs.push({
        attribute: factor.id,
        label: this.adjusterPresetsService.getLabelInCurrentLang(factor.labels),
        displayFunction: decimal2prettyPerc,
      });
    }
    attrs.push({
      attribute: 'NUTS_ID',
      label: 'Detailed report',
      displayFunction: (x) => {
        return `<a href="${this.serviceUrl}${x}" target="_blank">in a new page</a>.`;
      },
    });
    nuts3IndexLayer.set('popUp', {
      attributes: attrs,
    });
    //nuts3IndexLayer?.getSource()?.getFeatures()?.[0]?.dispatchEvent('change');
  }

  //FIXME: how to do this??
  updateClusterLayerPopUps() {
    /*return {
      popUp: {
        attributes: [
          {attribute: 'CNTR_CODE', label: 'Country'},
          {attribute: 'NUTS_NAME', label: 'Name'},
          {
            attribute: method.codename,
            label: 'Cluster ID',
          },
          {
            attribute: 'NUTS_ID',
            label: 'Detailed report',
            displayFunction: (x) => {
              return `<a href="${this.serviceUrl}${x}" target="_blank">in a new page</a>.`;
            },
          },
        ],
      },
    };*/
  }

  /**
   * Updates the color palette with and appropriate number of colors
   * @param count - number of colors to generate
   */
  refreshColorPalette(count: number): void {
    this.colorPalette = this.generateRandomColorPalette(count);
    this.staticClusterLegend = this.colorPalette.slice(0, count);
  }

  /**
   * https://martin.ankerl.com/2009/12/09/how-to-create-random-colors-programmatically/
   * @private
   * @description Only generates random colors if the current palette does not provide enough colors for all the clusters
   * @param {number} colorCount Number of colors to randomly generate
   * @returns {Array<Array<number> | string>} Array of RGB colors
   */
  private generateRandomColorPalette(colorCount: number): Array<string> {
    const palette = this.colorPalette;
    const goldenRatioConjugate = 0.618033988749895;
    let i = palette.length;
    while (i < colorCount) {
      let h = Math.random();
      h += goldenRatioConjugate;
      h %= 1;
      h *= 360;
      const [r, g, b] = hsv2rgb(h, 0.5, 0.95);
      palette.push(`rgba(${r}, ${g}, ${b}, 1)`);
      i++;
    }
    return palette;
  }
}
